import cx from 'classnames';
import { forwardRef } from 'react';
import FloorPlanTable from '@components/floorPlan/FloorPlanTable';
import type { TableIconCommonProps } from '@components/floorPlan/InteractiveFloorPlanTable';
import typography from '@shared/styles/typography.scss';
import { getAnonymousName } from '@shared/utils/formatName';
import { ISOTimeTo12HourTime } from '@shared/utils/time';
import type {
  AssignedServer,
  SeatedOccupant,
  TableTimer,
} from '../../../floorPlans/apiHelpers';
import { ServiceStatusIcon } from '../serviceStatus/ServiceStatusIcon';
import { ServerBadge } from '../sidePanel/servers/ServerBadge';
import { useReservationServiceContext } from '../state/ReservationServiceContext';
import styles from './SeatedFloorPlanTable.scss';
import { SERVICE_STATUS_COLORS } from '../serviceStatus/ServiceStatus';
import { formatTableSpend, getTableSpend } from './tableSpend';

export interface SeatedFloorPlanTableProps extends TableIconCommonProps {
  assignedServer?: AssignedServer | null;
  seatedOccupant: SeatedOccupant;
  isDisabled: boolean;
  isHighlighted: boolean;
  isAnyTableHighlighted: boolean;
  handleTableOnClick?: () => void;
  timer?: TableTimer | null;
}

const HARDCODED_SCALE_OVERRIDE = 0.33;

export const SeatedFloorPlanTable = forwardRef<
  HTMLDivElement,
  SeatedFloorPlanTableProps
>(
  (
    {
      assignedServer,
      handleTableOnClick,
      iconName,
      iconWidthScale,
      isAnyTableHighlighted,
      isDisabled,
      isHighlighted,
      left,
      name,
      orientation,
      seatedOccupant,
      tableIconScale,
      testId,
      timer,
      top,
    },
    ref,
  ) => {
    const fullName = getAnonymousName(
      seatedOccupant.firstName,
      seatedOccupant.lastName,
    );
    const tooltip = `${fullName} seated at table ${name}, party of ${seatedOccupant.guestCount}`;

    const {
      occupants,
      shouldShowServerBadges,
      shouldShowServiceStatuses,
      shouldShowSpend,
      shouldShowTableNumbers,
      timerDisplayMode,
    } = useReservationServiceContext();
    const spend = shouldShowSpend
      ? getTableSpend(occupants, seatedOccupant)
      : null;

    const iconClassNames = cx({
      [styles.serviceStatusIcon]: true,
      [styles.isHighlighted]: isHighlighted && isAnyTableHighlighted,
      [styles.isDimmed]: !isHighlighted && isAnyTableHighlighted,
    });

    const shouldShowServerBadge = assignedServer && shouldShowServerBadges;
    const shouldShowCompoundRow =
      shouldShowServiceStatuses || shouldShowServerBadge;
    return (
      <FloorPlanTable
        left={left}
        top={top}
        tableIconScale={tableIconScale}
        data-testid={testId}
        ref={ref}
        title={tooltip}
      >
        <button
          aria-label={`View details for table ${name}`}
          className={styles.button}
          disabled={isDisabled}
          onClick={handleTableOnClick}
          type="button"
        >
          <FloorPlanTable.Icon
            iconName={iconName}
            iconWidthScale={iconWidthScale}
            isHighlighted={isHighlighted}
            orientation={orientation}
            tableIconScale={tableIconScale}
          />

          <div className={styles.overlay}>
            {shouldShowCompoundRow && (
              <div className={styles.row}>
                {shouldShowServiceStatuses && (
                  <span
                    className={styles.serverStatusBackground}
                    style={{
                      backgroundColor:
                        SERVICE_STATUS_COLORS[seatedOccupant.serviceStatus],
                    }}
                  >
                    <ServiceStatusIcon
                      serviceStatus={seatedOccupant.serviceStatus}
                      className={iconClassNames}
                      scale={HARDCODED_SCALE_OVERRIDE}
                    />
                  </span>
                )}

                {shouldShowServerBadge && (
                  <ServerBadge
                    variant="floorPlanYAxisStack"
                    serverName={assignedServer.name}
                    badgeColor={assignedServer.hexColor}
                  />
                )}
              </div>
            )}

            {shouldShowTableNumbers && (
              <div
                className={cx(styles.row, typography.t3_12, styles.tableName)}
              >
                {name}
              </div>
            )}

            {spend !== null && (
              <div className={cx(styles.row, typography.t3_12, styles.spend)}>
                {formatTableSpend(spend)}
              </div>
            )}

            {timer && timerDisplayMode === 'countdown' && (
              <div className={cx(styles.row, typography.t3_12, styles.timer)}>
                {timer.timer}
              </div>
            )}
            {timer && timerDisplayMode === 'clockTime' && (
              <div className={cx(styles.row, typography.t3_12, styles.timer)}>
                {ISOTimeTo12HourTime(timer.upcomingReservation.time)}
              </div>
            )}
          </div>
        </button>
      </FloorPlanTable>
    );
  },
);

SeatedFloorPlanTable.displayName = 'SeatedFloorPlanTable';
